import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useAPI } from "lib/API";
import ErrorMessage from "view/ErrorMessage";
import FeatureFlag from "data/FeatureFlag";
import Vendor from "data/Vendor";
import LoadingIndicator from "components/UI/LoadingIndicator";
import FlexboxContainerComponent from "components/UI/FlexboxContainer";
import { ERROR_MESSAGES } from "constants/emailConfiguration";
import { renderToast, toast } from "components/UI/ToastNotification";
import { BusinessUnit } from "data/BusinessUnit";
import AutomationSettingTab from "components/Brand/AutomationSettingTab";
import { loadBrandVendor, updateBrandVendorConfiguration } from "requests/brand";

const EmptyMessage = styled.span`
  font-size: 20px;
  padding: 0px;
`;

interface AutomationSettingsWrapperProps {
    featureFlags: FeatureFlag;
    selectedVendor: Vendor;
    selectedBusinessUnit?: BusinessUnit;
    automationSettingsReadOnly: boolean;
}

const AutomationSettingsWrapper = React.forwardRef<
    { handleSave: () => void },
    AutomationSettingsWrapperProps
>(({ featureFlags, selectedVendor, selectedBusinessUnit, automationSettingsReadOnly }, ref) => {
    const [brandData, setBrandData] = useState<any>(null);
    const [isSlbVendor, setIsSlbVendor] = useState<boolean>(false);
    const [buyNowCredentials, setBuyNowCredentials] = useState<{
        clientId: string;
        clientSecret: string;
        clientUrl: string;
        buyNowConsumerKey: string;
        brandCode: string;
    } | null>(null);

    const brandVendorReq = useAPI({
        deferFn: loadBrandVendor,
        onResolve: (data) => {
            setBrandData({
                ...data,
                isMaterialApiEnabled: data.isMaterialApiEnabled ?? false,
                isStockApiEnabled: data.isStockApiEnabled ?? false,
                isSalesApiEnabled: data.isSalesOrderApiEnabled ?? false,
                isBuyNowEnabled: data.isBuyNowEnabled ?? false,
                buyNowConsumerKey: data.buyNowConsumerKey || '',
            });
        },
    });

    const updateBrandVendorReq = useAPI({
        deferFn: updateBrandVendorConfiguration,
        onResolve: () => {
            renderToast(toast.TYPE.SUCCESS, "Brand configuration updated.");
        },
        onReject: (err) => {
            renderToast(toast.TYPE.ERROR, err.message);
        },
    });

    // Expose handleSave via ref
    React.useImperativeHandle(ref, () => ({
        handleSave: () => {
            if (brandData) {
                const payload = {
                    ...brandData,
                    isSlbVendor,
                    buyNowConsumerKey: buyNowCredentials?.buyNowConsumerKey || ""
                }
                updateBrandVendorReq.run(selectedVendor?.id, selectedBusinessUnit?.brand?.id, payload);
            }
        },
    }));

    useEffect(() => {
        if (selectedVendor?.id && selectedBusinessUnit?.brand?.id) {
            brandVendorReq.run(selectedVendor?.id, selectedBusinessUnit?.brand?.id);
        }

        if (selectedVendor?.id) {
            setIsSlbVendor(selectedVendor?.isSlbVendor!);
        }
    }, [selectedVendor?.id, selectedBusinessUnit?.brand?.id]);

    if (!selectedVendor) {
        return (
            <FlexboxContainerComponent flexDirection="column">
                <EmptyMessage>{ERROR_MESSAGES.NO_VENDOR_AUTOMATION_SETTINGS}</EmptyMessage>
            </FlexboxContainerComponent>
        );
    }

    if (!selectedBusinessUnit) {
        return (
            <FlexboxContainerComponent flexDirection="column">
                <EmptyMessage>{ERROR_MESSAGES.NO_BUSINESS_UNIT_AUTOMATION_SETTINGS}</EmptyMessage>
            </FlexboxContainerComponent>
        );
    }

    return (
        <>
            {brandVendorReq.isPending && <LoadingIndicator center />}
            {brandVendorReq.error && <ErrorMessage message={brandVendorReq.error.message} />}

            {brandData && (
                <AutomationSettingTab
                    brand={brandData}
                    setBrand={setBrandData}
                    featureFlags={featureFlags}
                    hasEditRights={!automationSettingsReadOnly}
                    selectedVendor={selectedVendor}
                    isSlbVendor={isSlbVendor}
                    setIsSlbVendor={setIsSlbVendor}
                    buyNowCredentials={buyNowCredentials}
                    setBuyNowCredentials={setBuyNowCredentials}
                />
            )}
        </>
    );
});

export default AutomationSettingsWrapper;
